import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTracking } from 'react-tracking';
import { push } from 'redux-first-router';
import loadable from '@loadable/component';

import { useSnackbar } from '@/features/snackbar';

import { PORTFOLIOS_QUERY_KEY } from '@/hooks/portfolio/usePortfolio';
import { AGGREGATE_PORTFOLIO_QUERY_KEY } from '@/hooks/portfolio/useAggregatePortfolio';

import { getPath } from '@/router/utils';

import { ROUTE_PORTFOLIO, ROUTE_PORTFOLIO_V2 } from '@/constants/routes';

import { Brokerages } from '../BrokeragesSearch';

import type { Brokerage } from '../../hooks/useBrokerSearch';
import type { PortfolioLinkingSDKError } from '../../../LinkingProviders/error/PortfolioLinkingSDKError';
import { isPortfolioLinkingSDKError } from '../../../LinkingProviders/error/PortfolioLinkingSDKError';
import { LinkingSDKError } from '../LinkingSDKError';
import { capturePortfolioLinkingError } from '../../../LinkingProviders/utils/capturePortfolioLinkingError';

type Props = {
  brokerage: Brokerage | undefined;
  setBrokerage: (brokerage: Brokerage | undefined) => void;
};

const LinkingWithInstructions = loadable(
  () => import('./LinkingWithInstructions'),
  {
    resolveComponent: (component) => component.LinkingWithInstructions,
  }
);

const PlaidPortfolioConnector = loadable(
  () => import('./../../../LinkingProviders/Plaid/PlaidPortfolioConnector'),
  {
    resolveComponent: (component) => component.PlaidPortfolioConnector,
  }
);
const PassivPortfolioConnector = loadable(
  () => import('../../../LinkingProviders/Passiv'),
  {
    resolveComponent: (component) => component.PassivPortfolioConnector,
  }
);
const SharesightPortfolioConnector = loadable(
  () => import('../../../LinkingProviders/Sharesight'),
  {
    resolveComponent: (component) => component.SharesightPortfolioConnector,
  }
);

export const CreateLinkedPortfolio = ({ brokerage, setBrokerage }: Props) => {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();
  const [linkingSDKError, setLinkingSDKError] =
    useState<PortfolioLinkingSDKError | null>(null);

  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'connectWithBrokerage',
  });

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: PORTFOLIOS_QUERY_KEY,
    });
    queryClient.invalidateQueries({
      queryKey: AGGREGATE_PORTFOLIO_QUERY_KEY,
    });
  }, [queryClient]);

  const handleClose = useCallback(() => {
    invalidateQueries();
    trackEvent({ action: 'closed' });
    setBrokerage(undefined);
  }, [invalidateQueries, setBrokerage, trackEvent]);

  const handleSuccess = useCallback(
    (data: { portfolioId?: string }) => {
      invalidateQueries();
      trackEvent({ action: 'complete' });
      addSnackbar({
        type: 'positive',
        lifeTime: 2000,
        message: 'Portfolio successfully imported from your brokerage',
      });

      typeof data.portfolioId === 'undefined'
        ? push(
            getPath({
              type: ROUTE_PORTFOLIO_V2,
              payload: {},
            })
          )
        : push(
            getPath({
              type: ROUTE_PORTFOLIO,
              payload: { portfolioId: data.portfolioId },
            })
          );
    },
    [invalidateQueries, trackEvent, addSnackbar]
  );

  /**
   * Note: This event handler aims to unify the error handling for all linking providers,
   * covering both SDK errors and SWS errors during the linking flow.
   */
  const handleError = useCallback(
    (error?: PortfolioLinkingSDKError) => {
      if (isPortfolioLinkingSDKError(error) && brokerage) {
        trackEvent({ action: 'providerError' });
        capturePortfolioLinkingError({
          provider: brokerage.provider.name,
          brokerage: brokerage.name,
          flow: 'connection',
          error,
        });
        setBrokerage(undefined);
        setLinkingSDKError(error);
        return;
      }

      /**
       * NOTE: This is temporary until we introduce a new error type to cover the errors from SWS BE during linking flow.
       * Once its done, the following code will be executed after checking the PortfolioLinkingSWSError type.
       * Also we can show a better error message once we start using PortfolioLinkingSWSError
       */
      trackEvent({ action: 'error' });
      setBrokerage(undefined);
      addSnackbar({
        type: 'negative',
        lifeTime: 3000,
        message: 'Failed to import your portfolio from your brokerage',
      });
    },
    [brokerage, trackEvent, setBrokerage, addSnackbar]
  );

  const brokerageProviderName = brokerage?.provider.name;

  return (
    <Track>
      {linkingSDKError !== null && <LinkingSDKError error={linkingSDKError} />}
      <Brokerages onSelect={setBrokerage} brokerage={brokerage} />

      {brokerage && brokerageProviderName === 'passiv' && (
        <LinkingWithInstructions onClose={handleClose} brokerage={brokerage}>
          <PassivPortfolioConnector
            onError={handleError}
            onClose={handleClose}
            onSuccess={handleSuccess}
            brokerageId={brokerage.id}
          />
        </LinkingWithInstructions>
      )}

      {brokerage && brokerageProviderName === 'sharesight' && (
        <SharesightPortfolioConnector
          onError={handleError}
          brokerageId={brokerage.id}
        />
      )}

      {brokerage && brokerageProviderName === 'plaid' && (
        <PlaidPortfolioConnector
          onError={handleError}
          onClose={handleClose}
          onSuccess={handleSuccess}
          providerBrokerageId={brokerage.providerBrokerageId}
        />
      )}
    </Track>
  );
};
